/**
 * ChatBI Agent API
 * 处理与后端 Agent 的流式对话
 */

import type { ActionProposalData } from '@/api/actionProposal'
import type { TaskStatus } from '@/types/task'
import { tokenHolder } from '@/utils'
import {
  PlanConfirmResponseSchema,
  UploadResponseSchema,
} from '@/schemas/chatbi'
import { SSEClient } from '@/transport/sse-client'
const BASE_URL = '/api/v1/unified'

// SSE 事件类型
export type StreamEventType =
  | 'node'
  | 'content'
  | 'result'
  | 'intent'
  | 'intent_thinking'
  | 'execution_plan'
  | 'chart'
  | 'clarification'
  | 'workflow'
  | 'render_ui'
  | 'autonomy_task'
  | 'autonomy_chain'
  | 'autonomy_event'
  | 'autonomy_mode'
  | 'deep_research'
  | 'dashboard'
  | 'thinking'
  | 'tool_call'
  | 'tool_result'
  | 'answer'
  | 'action_proposal'
  | 'suggested_questions'
  | 'error'
export type NodeEventAction = 'start' | 'finish'
export type ContentEventAction = 'token'
export type NodeStatus = 'running' | 'completed' | 'failed' | 'pending'

// 工作流阶段类型
export type WorkflowPhase = 'thinking' | 'planning' | 'executing' | 'completed'

// 工作流阶段描述映射
export const WORKFLOW_PHASE_DESCRIPTIONS: Record<WorkflowPhase, string> = {
  thinking: '正在思考...',
  planning: '正在制定执行计划...',
  executing: '正在执行...',
  completed: '执行完成'
}

/**
 * 意图类型
 * 包含所有 Agent ID + 意图别名，与后端 router.py 保持一致
 */
export type IntentPrimary =
  | 'chat'
  | 'chatbi'
  | 'chart'
  | 'report'
  | 'announcement'
  | 'customer_service'
  | 'ops'
  | 'social'
  | 'social_share'
  | 'social_media'
  | 'research'
  | 'clarification'
  | 'smart_query'
  | 'data_query'
  | 'dashboard_builder'
  | 'dashboard_generation'
  | 'deep_insight'
  | 'chart_generate'
  | 'report_generate'
  | 'customer_service'

/** ChatBI 子流程节点类型 */
export type AgentTypeName = 'smart_query' | 'dashboard_builder' | 'deep_insight'

/** 旧值类型（兼容历史数据） */
export type LegacyIntentType = 'data_query' | 'chart_generate' | 'report_generate'

// ========================================
// 新旧值映射关系
// ========================================
// 旧值 → 新值（用于处理历史数据）
export const LEGACY_TO_NEW_MAP: Record<LegacyIntentType, IntentPrimary> = {
  data_query: 'smart_query',
  chart_generate: 'dashboard_builder',
  report_generate: 'deep_insight'
}

// 新值 → 旧值（用于向后兼容）
export const NEW_TO_LEGACY_MAP: Record<AgentTypeName, LegacyIntentType> = {
  smart_query: 'data_query',
  dashboard_builder: 'chart_generate',
  deep_insight: 'report_generate'
}

/**
 * 标准化意图类型（将旧值转换为新值）
 * 支持新旧值混用场景
 */
export function normalizeIntentType(intent: string): IntentPrimary {
  // 如果是旧值，转换为新值
  if (intent in LEGACY_TO_NEW_MAP) {
    return LEGACY_TO_NEW_MAP[intent as LegacyIntentType]
  }
  if (intent === 'dashboard_generation') {
    return 'dashboard_builder'
  }
  // 如果是新值或其他值，直接返回
  return intent as IntentPrimary
}

/**
 * 检查是否为智能问数类型（支持新旧值）
 */
export function isSmartQuery(intent: string): boolean {
  return intent === 'smart_query' || intent === 'data_query'
}

/**
 * 检查是否为看板搭建类型（支持新旧值）
 */
export function isDashboardBuilder(intent: string): boolean {
  return (
    intent === 'dashboard_builder' ||
    intent === 'dashboard_generation' ||
    intent === 'chart_generate'
  )
}

/**
 * 检查是否为深度洞察类型（支持新旧值）
 */
export function isDeepInsight(intent: string): boolean {
  return intent === 'deep_insight' || intent === 'report_generate'
}

// 节点事件
export interface NodeStreamEvent {
  type: 'node'
  event: NodeEventAction
  node_id: string
  node_name: string
  status: NodeStatus
  title?: string
  duration_ms?: number
  data?: Record<string, unknown>
  explanation?: string // 节点生成逻辑说明
}

// 内容事件
export interface ContentStreamEvent {
  type: 'content'
  event: ContentEventAction
  content: string
}

// 思考事件
export interface ThinkingStreamEvent {
  type: 'thinking'
  content: string
}

// 工具调用事件
export interface ToolCallStreamEvent {
  type: 'tool_call'
  event: 'start' | 'finish'
  tool?: string
  message?: string
  result?: string
}

export interface ToolResultStreamEvent {
  type: 'tool_result'
  name: string
  content?: string
  call_id?: string
  tool_args?: Record<string, unknown>
  metadata?: Record<string, unknown>
}

export interface AnswerStreamEvent {
  type: 'answer'
  content?: string
  answer?: string
  session_id?: string
  trace_id?: string
  metadata?: Record<string, unknown>
}

// 意图事件
export interface IntentStreamEvent {
  type: 'intent'
  event: 'start' | 'finish'
  message?: string
  operation_type?: string
  sub_intent?: string | null
  target_agent?: string
  target_agent_name?: string // 路由目标 Agent 的显示名称
  target_agent_icon?: string // 路由目标 Agent 的图标（emoji）
  target_agent_color?: string // 路由目标 Agent 的主题色（十六进制）
  completed_query?: string
  reason?: string
  duration_ms?: number
  execution_plan?: ExecutionPlanData
}

// 意图思考步骤事件（后端分析管线实时推送的思考进度）
export interface IntentThinkingStreamEvent {
  type: 'intent_thinking'
  event: 'step'
  /** 步骤标识：understanding | context | intent_detection | intent_result | chat_detected | deep_analysis | analysis_result | skill_matching | routing */
  step: string
  /** 步骤描述文字 */
  message: string
  /** 可选的详细信息（渲染为步骤下方的补充说明） */
  detail?: string
}

// 执行计划步骤
export interface ExecutionPlanStep {
  step_id: string
  step_type: string
  name: string
  description: string
  agent: string
  status: NodeStatus
  duration_ms: number
  is_optional: boolean
  condition: string
  sub_steps: ExecutionPlanStep[]
  error: string | null
}

// 执行计划意图
export interface ExecutionPlanIntent {
  category: string
  primary: IntentPrimary
  operation_type: string
  sub_intent: string | null
}

// 执行计划确认状态
export type PlanConfirmationStatus = 'auto_execute' | 'pending_confirm' | 'confirmed' | 'cancelled'

// 执行计划操作按钮
export interface PlanAction {
  id: string
  label: string
  type: 'primary' | 'default' | 'danger'
}

// 执行计划数据
export interface ExecutionPlanData {
  plan_id: string
  query: string
  intent: ExecutionPlanIntent
  steps: ExecutionPlanStep[]
  current_step_id: string | null
  status: NodeStatus
  confirmation_status: PlanConfirmationStatus
  requires_confirmation: boolean
  estimated_complexity: 'simple' | 'medium' | 'complex'
}

// 执行计划事件
export interface ExecutionPlanStreamEvent {
  type: 'execution_plan'
  event: 'created' | 'update' | 'pending' | 'confirmed' | 'cancelled'
  plan: ExecutionPlanData
  message?: string
  actions?: PlanAction[]
}

// 图表数据
export interface ChartData {
  chart_type: string
  title: string
  x_axis: string
  y_axis: string[]
  legend: string[]
  options?: {
    show_data_labels?: boolean
    stack?: boolean
    smooth?: boolean
  }
  data: Record<string, unknown>[]
}

// 分析数据
export interface AnalysisData {
  summary: string
  insights: string[]
  recommendations: string[]
  data_characteristics?: Record<string, unknown>
  basic_stats?: Record<string, unknown>
}

// 执行数据
export interface ExecutionData {
  success: boolean
  row_count: number
  columns: string[]
}

// 结果事件
export interface ResultStreamEvent {
  type: 'result'
  answer: string
  intent?: string
  operation_type?: string
  target_agent?: string
  target_agent_name?: string
  agent_id?: string
  agent_name?: string
  session_id?: string
  trace_id?: string
  sql?: string
  sql_explanation?: string // SQL 生成逻辑说明（用户可读）
  source_tables?: string[] // 数据来源表（后端从 SQL 解析，前端可直接展示）
  chart?: ChartData
  analysis?: AnalysisData
  execution?: ExecutionData
  // 原始数据（当 chart 数据缺失时可作为降级来源）
  data?: Record<string, unknown>[]
  columns?: string[]
  // 公告相关字段
  action_type?: string
  form_data?: Record<string, unknown>
  rxdata?: string | Record<string, unknown> | null
  query_data?: Record<string, unknown> // 公告查询结果（表格 + 详情）
  common_info?: Record<string, unknown>
  is_complete?: boolean
  nodes?: Array<Record<string, unknown>>
  formatted_form_config?: Record<string, unknown> // 表单配置 JSON
  formatted_markdown?: string // Markdown 格式的结果内容
  // 运营/社交分享字段
  ops_intent?: string
  content_items?: Array<{
    title?: string
    content: string
    platform?: string
    style?: string
    image_url?: string
    /** 素材类型: card=卡片 | image=图片 | link=链接 | landing_page=落地页 | poster=海报 */
    content_type?: 'card' | 'image' | 'link' | 'landing_page' | 'poster'
    /** 分享类型: app=应用分享 | mini_game=小游戏分享 */
    share_type?: 'app' | 'mini_game'
    /** 策略状态: active=启用 | disabled=禁用 | draft=草稿 */
    status?: 'active' | 'disabled' | 'draft'
    /** 关联埋点名称 */
    trigger_name?: string
    /** 落地页名称/URL */
    landing_page?: string
    /** 地区 */
    region?: string
    /** 权重 (1-9999) */
    weight?: number
    /** 策略名称 */
    strategy_name?: string
  }>
  // 看板 Markdown 数据（dashboard_builder 场景）
  // 后端 chatbi.py:436-446 在 result 事件里也会带上 dashboard_markdown，
  // 字段集合与独立的 type=dashboard 事件保持一致；composite_dashboard 是 v3
  // generate_dashboard 工具产出的结构化看板，前端必须保留以渲染网格布局。
  dashboard_markdown?: {
    content: string
    title?: string
    scenario?: string
    chart_types?: string[]
    generation_time?: number
    degraded?: boolean
    is_final?: boolean
    composite_dashboard?: Record<string, unknown> | null
    html_url?: string
  }
  // P1.11 洞察增强数据
  enriched_insights?: {
    primary_metrics?: string[]
    related_metrics?: string[]
    enriched_data?: Record<
      string,
      {
        metric_id: string
        metric_name: string
        data: Record<string, unknown>[]
        row_count: number
        sql?: string
      }
    >
    ab_test_group?: string
    skipped?: boolean
    degraded?: boolean
    error?: string
    reason?: string
  }

  // 归因分析结果（异常检测+下钻+根因+增强归因）
  attribution_result?: Record<string, unknown>

  // 置信度评分（1-5 星，由 response_composer 计算）
  confidence_score?: number
  // 置信度标签（较低/一般/较高/高/非常高）
  confidence_label?: string

  // 后续分析建议（由 Agent 生成）
  follow_up_suggestions?: string[]

  // ====== 社媒运营 Agent 专属字段 ======
  monitor_data?: {
    summary?: {
      total_mentions?: number
      positive_ratio?: number
      neutral_ratio?: number
      negative_ratio?: number
      trend?: string
    }
    top_keywords?: Array<{ keyword: string; count: number; sentiment: string }>
    alerts?: Array<{ message: string }>
    platform?: string
    period?: string
  }
  hot_topics?: Array<{
    keyword: string
    score: number
    sentiment: string
    appearance_count: number
    platforms?: string[]
  }>
  sentiment_data?: {
    sentiment: string
    confidence: number
    summary?: string
  }

  // ====== 调研分析 Agent 专属字段 ======
  search_results?: Array<{
    title: string
    content: string
    url?: string
  }>
  report_markdown?: string
  metrics_summary?: {
    metrics?: Record<string, string | number>
    key_findings?: string[]
    findings?: string[]
    conclusion?: string
    summary?: string
  }
}

// 图表事件（dashboard_builder 类型专用）
export interface ChartStreamEvent {
  type: 'chart'
  event: 'start' | 'finish'
  chart?: ChartData
  data?: Record<string, unknown>[]
  columns?: string[]
}

// 澄清数据
export interface ClarificationData {
  question: string // 澄清问题
  suggestions: string[] // 建议选项
  can_infer?: boolean // 是否能推断用户意图
  inferred_query?: string // 推断的查询
  response_type?: 'confirm' | 'ask' // 响应类型：确认 or 追问
}

// 澄清事件
export interface ClarificationStreamEvent {
  type: 'clarification'
  question: string
  suggestions: string[]
  can_infer?: boolean
  inferred_query?: string
  response_type?: 'confirm' | 'ask'
}

// 工作流事件（包括开始、结束、阶段变更）
export interface WorkflowStreamEvent {
  type: 'workflow'
  event: 'start' | 'finish' | 'phase' | 'cancelled'
  trace_id: string
  // phase 事件特有字段
  phase?: WorkflowPhase
  message?: string
  progress?: number
  // finish 事件特有字段
  status?: string
  duration_ms?: number
  result?: Record<string, unknown>
}

// 深度研究事件子类型
export type DeepResearchEventName =
  | 'started'
  | 'step_started'
  | 'step_progress'
  | 'step_completed'
  | 'step_failed'
  | 'report_generating'
  | 'report_section'
  | 'completed'
  | 'failed'

// 深度研究 SSE 事件
export interface DeepResearchStreamEvent {
  type: 'deep_research'
  event: DeepResearchEventName
  data: Record<string, unknown>
}

// 看板生成事件（后端 dashboard_builder 流程完成后发送）
export interface DashboardStreamEvent {
  type: 'dashboard'
  event: 'start' | 'finish'
  dashboard_markdown: {
    content: string
    title?: string
    scenario?: string
    chart_types?: string[]
    generation_time?: number
    degraded?: boolean
    is_final?: boolean
    html_url?: string
    /**
     * v3 generate_dashboard 产出的结构化看板数据，供 CompositeDashboardGrid
     * 渲染真正的网格看板；缺省时 fallback 到 markdown content 线性渲染。
     * 后端注入路径：chatbi.py:445。
     */
    composite_dashboard?: Record<string, unknown> | null
  }
  data?: Record<string, unknown>[]
  columns?: string[]
}

/** 统一入口 SSE：业务/系统错误说明（与 HTTP 异常区分，需映射为 result.answer 供会话展示） */
export interface ErrorStreamEvent {
  type: 'error'
  message?: string
  trace_id?: string
}

/** 行动建议事件（由 action_suggest 节点透出） */
export type ActionProposalStreamEvent = ActionProposalData & {
  type: 'action_proposal'
}

// 统一事件类型
export type StreamEvent =
  | NodeStreamEvent
  | ContentStreamEvent
  | ResultStreamEvent
  | IntentStreamEvent
  | IntentThinkingStreamEvent
  | ExecutionPlanStreamEvent
  | ChartStreamEvent
  | ClarificationStreamEvent
  | WorkflowStreamEvent
  | RenderUIStreamEvent
  | AutonomyTaskStreamEvent
  | AutonomyChainStreamEvent
  | AutonomyEventStreamEvent
  | AutonomyModeStreamEvent
  | DeepResearchStreamEvent
  | DashboardStreamEvent
  | ThinkingStreamEvent
  | ToolCallStreamEvent
  | ToolResultStreamEvent
  | AnswerStreamEvent
  | ActionProposalStreamEvent
  | ErrorStreamEvent

// 聊天附件
export interface ChatAttachment {
  url: string // OSS URL
  filename: string // 原始文件名
  type: string // 文件类型: image/png, image/jpeg 等
  size: number // 文件大小（字节）
}

// 聊天请求参数
export interface ChatRequest {
  query: string
  project_id: string
  session_id: string
  user_id: string
  attachments?: ChatAttachment[] // 聊天附件列表
  target_agent?: string // 强制路由到指定 Agent（跳过意图识别，用于 Agent 预览页）
  agent_instance_id?: string // 指定 Agent 实例；未选择时不发送，由后端使用基础 Agent
  mode?: string // 聊天模式：chat / deep_research / data_query / report_generate
  direct_llm?: boolean // 直接调用 LLM，跳过意图路由（用于 Prompt 优化等场景）
  initial_context?: Record<string, unknown> // 外部上下文注入，用于工作台组件追问等场景
}

// 节点执行状态
export interface NodeExecutionState {
  node_id: string
  node_name: string
  status: NodeStatus
  start_time?: number
  end_time?: number
  duration_ms?: number
  title?: string
  data?: Record<string, unknown>
}

// T-04 A2UI 动态组件渲染事件（render_ui）
export interface RenderUIStreamEvent {
  type: 'render_ui'
  event: 'render_ui'
  data: {
    component_type: 'MetricCard' | 'LineChart' | 'BarChart' | 'PieChart' | 'DataTable'
    props: {
      title: string
      data: Record<string, unknown>[]
      columns?: string[]
      truncated?: boolean
      total_rows?: number
    }
    action_id: string
    actions: Array<{
      trigger: string
      description: string
      params_template: Record<string, string>
    }>
  }
}

// ========== 自主模式 SSE 事件类型（P6.14）==========

// 自主任务状态变更事件
export interface AutonomyTaskStreamEvent {
  type: 'autonomy_task'
  event: 'created' | 'started' | 'completed' | 'failed'
  task_id: string
  project_id: string
  agent_type: string
  task_type: string
  status: string
  error_message?: string
  result?: Record<string, unknown>
}

// 协作链触发事件
export interface AutonomyChainStreamEvent {
  type: 'autonomy_chain'
  event: 'triggered' | 'completed'
  chain_id: string
  chain_name: string
  source_agent: string
  target_agent: string
  downstream_task_ids?: string[]
}

// 跨 Agent 事件状态事件
export interface AutonomyEventStreamEvent {
  type: 'autonomy_event'
  event: 'published' | 'consumed' | 'failed' | 'expired'
  event_id: string
  source_agent: string
  target_agent: string
  event_type: string
  project_id: string
}

// 自主模式切换/统计更新事件
export interface AutonomyModeStreamEvent {
  type: 'autonomy_mode'
  event: 'level_changed' | 'stats_update'
  project_id: string
  level?: string
  stats?: {
    running: number
    pending: number
    failed: number
    total_today: number
  }
}

// 聊天响应回调
export interface ChatStreamCallbacks {
  onThinking?: (event: ThinkingStreamEvent) => void
  onToolCallStart?: (event: ToolCallStreamEvent) => void
  onToolCallFinish?: (event: ToolCallStreamEvent) => void
  onNodeStart?: (event: NodeStreamEvent) => void
  onNodeFinish?: (event: NodeStreamEvent) => void
  onContentToken?: (content: string) => void
  onResult?: (event: ResultStreamEvent) => void
  onIntentStart?: (event: IntentStreamEvent) => void
  onIntentFinish?: (event: IntentStreamEvent) => void
  onIntentThinking?: (event: IntentThinkingStreamEvent) => void
  onExecutionPlanCreated?: (event: ExecutionPlanStreamEvent) => void
  onExecutionPlanUpdate?: (event: ExecutionPlanStreamEvent) => void
  onExecutionPlanPending?: (event: ExecutionPlanStreamEvent) => void
  onExecutionPlanConfirmed?: (event: ExecutionPlanStreamEvent) => void
  onExecutionPlanCancelled?: (event: ExecutionPlanStreamEvent) => void
  onChartStart?: (event: ChartStreamEvent) => void
  onChartFinish?: (event: ChartStreamEvent) => void
  onClarification?: (event: ClarificationStreamEvent) => void // 澄清事件回调
  // 工作流阶段回调
  onWorkflowStart?: (event: WorkflowStreamEvent) => void
  onWorkflowPhase?: (event: WorkflowStreamEvent) => void
  onWorkflowFinish?: (event: WorkflowStreamEvent) => void
  onRenderUI?: (event: RenderUIStreamEvent) => void // T-04 A2UI 动态组件渲染
  // 自主模式事件回调（P6.14）
  onAutonomyTask?: (event: AutonomyTaskStreamEvent) => void
  onAutonomyChain?: (event: AutonomyChainStreamEvent) => void
  onAutonomyEvent?: (event: AutonomyEventStreamEvent) => void
  onAutonomyMode?: (event: AutonomyModeStreamEvent) => void
  // 深度研究事件回调（U-02）
  onDeepResearch?: (event: DeepResearchStreamEvent) => void
  // 看板生成完成事件
  onDashboardFinish?: (event: DashboardStreamEvent) => void
  // Action Proposal 事件（ChatBI 行动建议卡片）
  onActionProposal?: (proposal: ActionProposalData) => void
  onSuggestedQuestions?: (suggestions: string[]) => void  // 推荐追问后补事件
  onError?: (error: Error) => void
  onComplete?: () => void
}

/**
 * 构建统一聊天请求 payload（净化 user_id 字段）
 * - 有 token 时保留 user_id
 * - 无 token 时移除 user_id（防止未认证请求携带用户标识）
 */
export function buildUnifiedChatRequestPayload<T extends ChatRequest>(
  request: T
): Record<string, unknown> {
  const { user_id, ...rest } = request
  if (tokenHolder.hasAccessToken()) {
    return { ...rest, user_id }
  }
  return { ...rest }
}

// ========== Phase 39: 统一 Chat API ==========

/**
 * routing SSE 事件类型
 * 后端 /chat/send 首先发送此事件，告知前端当前走的模式
 */
export interface RoutingStreamEvent {
  type: 'routing'
  mode: 'stream' | 'long_task'
  agent?: string
  target_agent?: string
  target_agent_name?: string
  agent_id?: string
  agent_name?: string
  task_id?: string
  squad_template?: string // Phase 75：Squad 模板名称（如 "望舒智囊团"）
}

/**
 * 路由事件回调：前端据此切换 UI 模式
 */
export interface SendChatCallbacks extends ChatStreamCallbacks {
  /** 收到 routing 事件时触发，前端据此决定 UI 模式 */
  onRouting?: (event: RoutingStreamEvent) => void
  /** 收到 dashboard_progress 事件时触发，供结构化看板渐进渲染 */
  onDashboardProgress?: (event: Record<string, unknown>) => void
  /** 收到 task_submitted 事件时触发（长任务路径） */
  onTaskSubmitted?: (event: {
    task_id: string
    status: TaskStatus
    session_id: string
    mode: string
    message: string
    resumable: boolean
  }) => void
}

interface ChatHttpErrorOptions {
  code?: string
  retryAfterSeconds?: number
}

export class ChatHttpError extends Error {
  status: number
  code?: string
  retryAfterSeconds?: number

  constructor(message: string, status: number, options: ChatHttpErrorOptions = {}) {
    super(message)
    this.name = 'ChatHttpError'
    this.status = status
    this.code = options.code
    this.retryAfterSeconds = options.retryAfterSeconds
  }
}

function isResponseRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function readErrorDetail(data: unknown): Record<string, unknown> {
  if (!isResponseRecord(data)) {
    return {}
  }
  const detail = data.detail
  if (isResponseRecord(detail)) {
    return detail
  }
  if (typeof detail === 'string' && detail.trim()) {
    return { message: detail }
  }
  return data
}

function readErrorString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined
}

function readErrorNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }
  return undefined
}

async function buildChatHttpError(response: Response): Promise<ChatHttpError> {
  let detail: Record<string, unknown> = {}
  try {
    detail = readErrorDetail(await response.clone().json())
  } catch {
    detail = {}
  }
  const code = readErrorString(detail.code)
  const message = readErrorString(detail.message) ?? `HTTP error! status: ${response.status}`
  const retryAfterSeconds = readErrorNumber(detail.retry_after_seconds)
    ?? readErrorNumber(response.headers.get('retry-after'))
  return new ChatHttpError(message, response.status, { code, retryAfterSeconds })
}

/**
 * 统一聊天入口（Phase 39）
 *
 * 前端只调这一个函数，后端全权决定走流式 or 长任务。
 * 通过 SSE routing 事件告知前端当前模式。
 *
 * @param request - 聊天请求参数
 * @param callbacks - 流式响应回调（含 routing 事件）
 * @param signal - AbortSignal，用于取消请求
 */
export async function sendChat(
  request: ChatRequest,
  callbacks: SendChatCallbacks,
  signal?: AbortSignal
): Promise<void> {
  const url = `${BASE_URL}/chat/send`
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(request.project_id && { Cpid: request.project_id }),
        ...(tokenHolder.hasAccessToken() && {
          Authorization: `Bearer ${tokenHolder.getAccessToken()}`
        })
      },
      body: JSON.stringify(
        buildUnifiedChatRequestPayload(request as ChatRequest & Record<string, unknown>)
      ),
      signal
    })

    if (!response.ok) {
      throw await buildChatHttpError(response)
    }

    if (!response.body) {
      throw new Error('Response body is null')
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    while (true) {
      const { done, value } = await reader.read()

      if (done) {
        callbacks.onComplete?.()
        break
      }

      buffer += decoder.decode(value, { stream: true })

      const lines = buffer.split('\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        const trimmedLine = line.trim()
        if (!trimmedLine) continue

        if (trimmedLine.startsWith('data: ')) {
          const jsonStr = trimmedLine.slice(6)
          if (!jsonStr.trim()) continue
          if (jsonStr.trim() === '[DONE]') {
            continue
          }

          let event: StreamEvent | RoutingStreamEvent | Record<string, unknown>
          try {
            event = JSON.parse(jsonStr) as
              | StreamEvent
              | RoutingStreamEvent
              | Record<string, unknown>
          } catch {
            logger.warn('[SSE] JSON 解析失败:', jsonStr.substring(0, 200))
            continue
          }

          try {
            // Phase 39: routing 事件 — 后端路由决策通知
            if ((event as Record<string, unknown>).type === 'routing') {
              callbacks.onRouting?.(event as RoutingStreamEvent)
              continue
            }

            // Phase 39: task_submitted 事件 — 长任务提交成功
            if ((event as Record<string, unknown>).type === 'task_submitted') {
              callbacks.onTaskSubmitted?.(
                event as {
                  task_id: string
                  status: TaskStatus
                  session_id: string
                  mode: string
                  message: string
                  resumable: boolean
                }
              )
              continue
            }

            // 复用 sendChatMessage 的事件分发逻辑
            _dispatchStreamEvent(event as StreamEvent, callbacks)
          } catch (callbackError) {
            logger.error(
              '[SSE] 事件回调执行异常:',
              (event as Record<string, unknown>).type,
              callbackError
            )
          }
        }
      }
    }
  } catch (error) {
    const normalizedError = error instanceof Error ? error : new Error(String(error))
    const isAbortLikeError = /abort|aborted|cancelled|canceled/i.test(
      `${normalizedError.name} ${normalizedError.message}`
    )
    if (isAbortLikeError) {
      console.info('Chat stream aborted by user action')
    } else {
      logger.error('Chat stream error:', normalizedError)
    }
    callbacks.onError?.(normalizedError)
  }
}

/**
 * 分发 SSE 事件到回调（从 sendChatMessage 中提取复用）
 * @internal
 */
function _dispatchStreamEvent(event: StreamEvent, callbacks: ChatStreamCallbacks): void {
  if (event.type === 'thinking') {
    callbacks.onThinking?.(event as ThinkingStreamEvent)
  } else if (event.type === 'tool_call') {
    const toolEvent = event as ToolCallStreamEvent
    if (toolEvent.event === 'start') {
      callbacks.onToolCallStart?.(toolEvent)
    } else if (toolEvent.event === 'finish') {
      callbacks.onToolCallFinish?.(toolEvent)
    }
  } else if (event.type === 'answer') {
    const resultEvent = buildAnswerResultEvent(event as AnswerStreamEvent)
    if (resultEvent) {
      callbacks.onContentToken?.(resultEvent.answer)
      callbacks.onResult?.(resultEvent)
    }
  } else if (event.type === 'node') {
    const nodeEvent = event as NodeStreamEvent
    if (nodeEvent.event === 'start') {
      callbacks.onNodeStart?.(nodeEvent)
    } else if (nodeEvent.event === 'finish') {
      callbacks.onNodeFinish?.(nodeEvent)
    }
  } else if (event.type === 'content') {
    const contentEvent = event as ContentStreamEvent
    if (contentEvent.event === 'token') {
      callbacks.onContentToken?.(contentEvent.content)
    }
  } else if (event.type === 'intent') {
    const intentEvent = event as IntentStreamEvent
    if (intentEvent.event === 'start') {
      callbacks.onIntentStart?.(intentEvent)
    } else if (intentEvent.event === 'finish') {
      callbacks.onIntentFinish?.(intentEvent)
    }
  } else if (event.type === 'intent_thinking') {
    callbacks.onIntentThinking?.(event as IntentThinkingStreamEvent)
  } else if (event.type === 'execution_plan') {
    const planEvent = event as ExecutionPlanStreamEvent
    if (planEvent.event === 'created') {
      callbacks.onExecutionPlanCreated?.(planEvent)
    } else if (planEvent.event === 'update') {
      callbacks.onExecutionPlanUpdate?.(planEvent)
    } else if (planEvent.event === 'pending') {
      callbacks.onExecutionPlanPending?.(planEvent)
    } else if (planEvent.event === 'confirmed') {
      callbacks.onExecutionPlanConfirmed?.(planEvent)
    } else if (planEvent.event === 'cancelled') {
      callbacks.onExecutionPlanCancelled?.(planEvent)
    }
  } else if (event.type === 'result') {
    const resultEvent = event as ResultStreamEvent
    const extendedEvent = event as unknown as {
      operation_type?: string
      suggestions?: string[]
      needs_clarification?: boolean
      clarification_session?: {
        session_id?: string
        type?: string
        suggestions?: string[]
      }
      data?: {
        clarification?: boolean
        question?: string
        suggestions?: string[]
        can_infer?: boolean
        inferred_query?: string
        response_type?: string
      }
    }
    const isClarification =
      extendedEvent.operation_type === 'clarification' ||
      extendedEvent.needs_clarification === true ||
      extendedEvent.data?.clarification === true

    if (isClarification) {
      const suggestions =
        extendedEvent.clarification_session?.suggestions ||
        extendedEvent.suggestions ||
        extendedEvent.data?.suggestions ||
        []
      const clarificationEvent: ClarificationStreamEvent = {
        type: 'clarification',
        question: extendedEvent.data?.question || resultEvent.answer || '',
        suggestions,
        can_infer: extendedEvent.data?.can_infer,
        inferred_query: extendedEvent.data?.inferred_query,
        response_type: extendedEvent.data?.response_type as 'confirm' | 'ask'
      }
      callbacks.onClarification?.(clarificationEvent)
    }
    callbacks.onResult?.(resultEvent)
  } else if (event.type === 'clarification') {
    const clarificationData = event as unknown as {
      type: 'clarification'
      question?: string
      suggestions?: string[]
      can_infer?: boolean
      inferred_query?: string
      response_type?: string
    }
    const clarificationEvent: ClarificationStreamEvent = {
      type: 'clarification',
      question: clarificationData.question || '',
      suggestions: clarificationData.suggestions || [],
      can_infer: clarificationData.can_infer,
      inferred_query: clarificationData.inferred_query,
      response_type: clarificationData.response_type as 'confirm' | 'ask'
    }
    callbacks.onClarification?.(clarificationEvent)
  } else if (event.type === 'chart') {
    const chartEvent = event as ChartStreamEvent
    if (chartEvent.event === 'start') {
      callbacks.onChartStart?.(chartEvent)
    } else if (chartEvent.event === 'finish') {
      callbacks.onChartFinish?.(chartEvent)
    }
  } else if (event.type === 'workflow') {
    const workflowEvent = event as WorkflowStreamEvent
    if (workflowEvent.event === 'start') {
      callbacks.onWorkflowStart?.(workflowEvent)
    } else if (workflowEvent.event === 'phase') {
      callbacks.onWorkflowPhase?.(workflowEvent)
    } else if (workflowEvent.event === 'finish' || workflowEvent.event === 'cancelled') {
      callbacks.onWorkflowFinish?.(workflowEvent)
    }
  } else if (event.type === 'render_ui') {
    callbacks.onRenderUI?.(event as RenderUIStreamEvent)
  } else if (event.type === 'autonomy_task') {
    callbacks.onAutonomyTask?.(event as AutonomyTaskStreamEvent)
  } else if (event.type === 'autonomy_chain') {
    callbacks.onAutonomyChain?.(event as AutonomyChainStreamEvent)
  } else if (event.type === 'autonomy_event') {
    callbacks.onAutonomyEvent?.(event as AutonomyEventStreamEvent)
  } else if (event.type === 'autonomy_mode') {
    callbacks.onAutonomyMode?.(event as AutonomyModeStreamEvent)
  } else if (event.type === 'deep_research') {
    callbacks.onDeepResearch?.(event as DeepResearchStreamEvent)
  } else if (event.type === 'dashboard') {
    const dashboardEvent = event as DashboardStreamEvent
    if (dashboardEvent.event === 'finish') {
      callbacks.onDashboardFinish?.(dashboardEvent)
    }
  } else if (event.type === 'error') {
    const errPayload = event as ErrorStreamEvent
    const msg = (errPayload.message || '').trim()
    if (msg) {
      callbacks.onResult?.({
        type: 'result',
        answer: msg,
        trace_id: errPayload.trace_id
      } as ResultStreamEvent)
    }
  }
}

function buildAnswerResultEvent(event: AnswerStreamEvent): ResultStreamEvent | null {
  const answer = event.content || event.answer || ''
  if (!answer.trim()) {
    return null
  }
  return {
    type: 'result',
    answer,
    session_id: event.session_id,
    trace_id: event.trace_id,
  }
}

/**
 * @deprecated Phase 39 后请使用 sendChat()，此函数将在 3 个版本后移除
 *
 * 发送聊天消息并处理流式响应
 * @param request - 聊天请求参数
 * @param callbacks - 流式响应回调
 * @param signal - AbortSignal，用于取消请求
 */
export async function sendChatMessage(
  request: ChatRequest,
  callbacks: ChatStreamCallbacks,
  signal?: AbortSignal
): Promise<void> {
  const url = `${BASE_URL}/chat/stream`

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        // 添加 Cpid header（MCP 项目 ID）
        ...(request.project_id && {
          Cpid: request.project_id
        }),
        // 添加 token 认证信息
        ...(tokenHolder.hasAccessToken() && {
          Authorization: `Bearer ${tokenHolder.getAccessToken()}`
        })
      },
      body: JSON.stringify(
        buildUnifiedChatRequestPayload(request as ChatRequest & Record<string, unknown>)
      ),
      signal
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    if (!response.body) {
      throw new Error('Response body is null')
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    while (true) {
      const { done, value } = await reader.read()

      if (done) {
        callbacks.onComplete?.()
        break
      }

      // 解码数据
      buffer += decoder.decode(value, { stream: true })

      // 按行分割处理
      const lines = buffer.split('\n')
      buffer = lines.pop() || '' // 保留最后一个不完整的行

      for (const line of lines) {
        const trimmedLine = line.trim()

        // 跳过空行
        if (!trimmedLine) continue

        // 处理 SSE 格式的数据
        if (trimmedLine.startsWith('data: ')) {
          const jsonStr = trimmedLine.slice(6) // 移除 'data: ' 前缀

          // 跳过空的 data 和结束标记
          if (!jsonStr.trim() || jsonStr.trim() === '[DONE]') continue

          // 先解析 JSON，再分发到回调（分开 try-catch，避免回调错误被静默吞掉）
          let event: StreamEvent
          try {
            event = JSON.parse(jsonStr) as StreamEvent
          } catch (parseError) {
            logger.warn('[SSE] JSON 解析失败:', jsonStr.substring(0, 200), parseError)
            continue
          }

          // 分发事件到回调（回调错误单独捕获并打印，不影响后续事件处理）
          try {
            if (event.type === 'thinking') {
              callbacks.onThinking?.(event as ThinkingStreamEvent)
            } else if (event.type === 'tool_call') {
              const toolEvent = event as ToolCallStreamEvent
              if (toolEvent.event === 'start') {
                callbacks.onToolCallStart?.(toolEvent)
              } else if (toolEvent.event === 'finish') {
                callbacks.onToolCallFinish?.(toolEvent)
              }
            } else if (event.type === 'answer') {
              const resultEvent = buildAnswerResultEvent(event as AnswerStreamEvent)
              if (resultEvent) {
                callbacks.onContentToken?.(resultEvent.answer)
                callbacks.onResult?.(resultEvent)
              }
            } else if (event.type === 'node') {
              const nodeEvent = event as NodeStreamEvent
              if (nodeEvent.event === 'start') {
                callbacks.onNodeStart?.(nodeEvent)
              } else if (nodeEvent.event === 'finish') {
                callbacks.onNodeFinish?.(nodeEvent)
              }
            } else if (event.type === 'content') {
              const contentEvent = event as ContentStreamEvent
              if (contentEvent.event === 'token') {
                callbacks.onContentToken?.(contentEvent.content)
              }
            } else if (event.type === 'intent') {
              const intentEvent = event as IntentStreamEvent
              if (intentEvent.event === 'start') {
                callbacks.onIntentStart?.(intentEvent)
              } else if (intentEvent.event === 'finish') {
                callbacks.onIntentFinish?.(intentEvent)
              }
            } else if (event.type === 'intent_thinking') {
              // 意图思考步骤事件：后端分析管线实时推送的思考进度
              const thinkingEvent = event as IntentThinkingStreamEvent
              callbacks.onIntentThinking?.(thinkingEvent)
            } else if (event.type === 'execution_plan') {
              const planEvent = event as ExecutionPlanStreamEvent
              if (planEvent.event === 'created') {
                callbacks.onExecutionPlanCreated?.(planEvent)
              } else if (planEvent.event === 'update') {
                callbacks.onExecutionPlanUpdate?.(planEvent)
              } else if (planEvent.event === 'pending') {
                callbacks.onExecutionPlanPending?.(planEvent)
              } else if (planEvent.event === 'confirmed') {
                callbacks.onExecutionPlanConfirmed?.(planEvent)
              } else if (planEvent.event === 'cancelled') {
                callbacks.onExecutionPlanCancelled?.(planEvent)
              }
            } else if (event.type === 'result') {
              const resultEvent = event as ResultStreamEvent
              // 检查是否是澄清事件
              // 方式1：operation_type === 'clarification'（后端直接返回）
              // 方式2：data.clarification === true（旧格式兼容）
              const extendedEvent = event as unknown as {
                operation_type?: string
                suggestions?: string[]
                needs_clarification?: boolean
                clarification_session?: {
                  session_id?: string
                  type?: string
                  suggestions?: string[]
                }
                data?: {
                  clarification?: boolean
                  question?: string
                  suggestions?: string[]
                  can_infer?: boolean
                  inferred_query?: string
                  response_type?: string
                }
              }
              const isClarification =
                extendedEvent.operation_type === 'clarification' ||
                extendedEvent.needs_clarification === true ||
                extendedEvent.data?.clarification === true

              if (isClarification) {
                // 转换为澄清事件
                // 优先从 clarification_session 获取 suggestions（后端新格式）
                const suggestions =
                  extendedEvent.clarification_session?.suggestions ||
                  extendedEvent.suggestions ||
                  extendedEvent.data?.suggestions ||
                  []
                const clarificationEvent: ClarificationStreamEvent = {
                  type: 'clarification',
                  question: extendedEvent.data?.question || resultEvent.answer || '',
                  suggestions,
                  can_infer: extendedEvent.data?.can_infer,
                  inferred_query: extendedEvent.data?.inferred_query,
                  response_type: extendedEvent.data?.response_type as 'confirm' | 'ask'
                }
                callbacks.onClarification?.(clarificationEvent)
              }
              callbacks.onResult?.(resultEvent)
            } else if (event.type === 'clarification') {
              // 直接处理 type: "clarification" 事件（后端从 router.py 返回的格式）
              const clarificationData = event as unknown as {
                type: 'clarification'
                question?: string
                suggestions?: string[]
                can_infer?: boolean
                inferred_query?: string
                response_type?: string
              }
              const clarificationEvent: ClarificationStreamEvent = {
                type: 'clarification',
                question: clarificationData.question || '',
                suggestions: clarificationData.suggestions || [],
                can_infer: clarificationData.can_infer,
                inferred_query: clarificationData.inferred_query,
                response_type: clarificationData.response_type as 'confirm' | 'ask'
              }
              callbacks.onClarification?.(clarificationEvent)
            } else if (event.type === 'chart') {
              const chartEvent = event as ChartStreamEvent
              if (chartEvent.event === 'start') {
                callbacks.onChartStart?.(chartEvent)
              } else if (chartEvent.event === 'finish') {
                callbacks.onChartFinish?.(chartEvent)
              }
            } else if (event.type === 'workflow') {
              const workflowEvent = event as WorkflowStreamEvent
              if (workflowEvent.event === 'start') {
                callbacks.onWorkflowStart?.(workflowEvent)
              } else if (workflowEvent.event === 'phase') {
                callbacks.onWorkflowPhase?.(workflowEvent)
              } else if (workflowEvent.event === 'finish' || workflowEvent.event === 'cancelled') {
                callbacks.onWorkflowFinish?.(workflowEvent)
              }
            } else if (event.type === 'render_ui') {
              // T-04 A2UI 动态组件渲染事件
              callbacks.onRenderUI?.(event as RenderUIStreamEvent)
            } else if (event.type === 'autonomy_task') {
              // 自主任务状态变更事件
              callbacks.onAutonomyTask?.(event as AutonomyTaskStreamEvent)
            } else if (event.type === 'autonomy_chain') {
              // 协作链触发事件
              callbacks.onAutonomyChain?.(event as AutonomyChainStreamEvent)
            } else if (event.type === 'autonomy_event') {
              // 跨 Agent 事件状态事件
              callbacks.onAutonomyEvent?.(event as AutonomyEventStreamEvent)
            } else if (event.type === 'autonomy_mode') {
              // 自主模式切换/统计更新事件
              callbacks.onAutonomyMode?.(event as AutonomyModeStreamEvent)
            } else if (event.type === 'deep_research') {
              callbacks.onDeepResearch?.(event as DeepResearchStreamEvent)
            } else if (event.type === 'dashboard') {
              const dashboardEvent = event as DashboardStreamEvent
              if (dashboardEvent.event === 'finish') {
                callbacks.onDashboardFinish?.(dashboardEvent)
              }
            } else if (event.type === 'action_proposal') {
              // ChatBI action_suggest 节点推送的行动建议事件
              callbacks.onActionProposal?.(event as unknown as ActionProposalData)
            } else if (event.type === 'error') {
              // 后端 Agent 以 type=error 透出业务/系统说明时，必须映射为 result，否则无 answer 会触发前端空结果兜底
              const errPayload = event as ErrorStreamEvent
              const msg = (errPayload.message || '').trim()
              if (msg) {
                callbacks.onResult?.({
                  type: 'result',
                  answer: msg,
                  trace_id: errPayload.trace_id
                } as ResultStreamEvent)
              }
            }
          } catch (callbackError) {
            // 回调错误不应该阻止后续事件处理，但需要明确打印以便排查
            logger.error('[SSE] 事件回调执行异常:', event.type, callbackError)
          }
        }
      }
    }
  } catch (error) {
    const normalizedError = error instanceof Error ? error : new Error(String(error))
    const isAbortLikeError = /abort|aborted|cancelled|canceled/i.test(
      `${normalizedError.name} ${normalizedError.message}`
    )

    if (isAbortLikeError) {
      console.info('Chat stream aborted by user action')
    } else {
      logger.error('Chat stream error:', normalizedError)
    }

    callbacks.onError?.(normalizedError)
  }
}

/**
 * 将毫秒转换为秒，保留2位小数
 */
export function formatDuration(durationMs: number): string {
  return (durationMs / 1000).toFixed(2) + 's'
}

// ========== 执行计划确认 API ==========

export interface PlanConfirmRequest {
  plan_id: string
  session_id?: string
  action: 'confirm' | 'cancel'
}

export interface PlanConfirmResponse {
  success: boolean
  plan_id: string
  action: string
  message: string
}

// ========== 认证 Header 工具函数 ==========

/** 构建带 Authorization + Content-Type: application/json 的请求头（用于 JSON 接口） */
function buildPlanHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  }
  if (tokenHolder.hasAccessToken()) {
    headers['Authorization'] = `Bearer ${tokenHolder.getAccessToken()}`
  }
  return headers
}

/** 构建仅含 Authorization 的请求头（用于 FormData 上传，不设 Content-Type 让浏览器自动填写 boundary） */
function buildAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {}
  if (tokenHolder.hasAccessToken()) {
    headers['Authorization'] = `Bearer ${tokenHolder.getAccessToken()}`
  }
  return headers
}

/**
 * 确认执行计划
 * @param planId 计划 ID
 * @param sessionId 会话 ID（可选）
 */
export async function confirmExecutionPlan(
  planId: string,
  sessionId?: string
): Promise<PlanConfirmResponse> {
  const url = `${BASE_URL}/plan/confirm`

  const response = await fetch(url, {
    method: 'POST',
    headers: buildPlanHeaders(),
    body: JSON.stringify({
      plan_id: planId,
      session_id: sessionId || '',
      action: 'confirm'
    } as PlanConfirmRequest)
  })

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  return PlanConfirmResponseSchema.parse(await response.json())
}

/**
 * 取消执行计划
 * @param planId 计划 ID
 * @param sessionId 会话 ID（可选）
 */
export async function cancelExecutionPlan(
  planId: string,
  sessionId?: string
): Promise<PlanConfirmResponse> {
  const url = `${BASE_URL}/plan/confirm`

  const response = await fetch(url, {
    method: 'POST',
    headers: buildPlanHeaders(),
    body: JSON.stringify({
      plan_id: planId,
      session_id: sessionId || '',
      action: 'cancel'
    } as PlanConfirmRequest)
  })

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  return PlanConfirmResponseSchema.parse(await response.json())
}

/**
 * 取消后台长任务
 * 停止按钮必须调用此接口，而不是仅断开前端 SSE 连接。
 * 优先使用 taskId 取消（/api/v1/tasks/{task_id}/cancel）；
 * 不传 taskId 时降级到 /api/v1/unified/chat/cancel 按 sessionId 查找活动任务并取消。
 */
export interface ChatCancelResponse {
  task_id: string | null
  session_id: string
  cancelled: boolean
  message: string
}

export async function cancelChatTask(
  sessionId: string,
  taskId?: string
): Promise<ChatCancelResponse> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  }
  if (tokenHolder.hasAccessToken()) {
    headers['Authorization'] = `Bearer ${tokenHolder.getAccessToken()}`
  }

  // 优先通过 task_id 取消（精确取消）
  if (taskId) {
    const url = `/api/v1/tasks/${taskId}/cancel`
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ session_id: sessionId })
      })
      if (!response.ok) {
        logger.warn(`[ChatBI] 通过 task_id 取消任务失败: HTTP ${response.status}`)
        return {
          task_id: taskId,
          session_id: sessionId,
          cancelled: false,
          message: `HTTP ${response.status}`
        }
      }
      return response.json()
    } catch (error) {
      logger.warn('[ChatBI] 取消任务请求异常，降级为前端中止:', error)
      return { task_id: taskId, session_id: sessionId, cancelled: false, message: String(error) }
    }
  }

  // 降级：通过 session_id 取消
  const url = `${BASE_URL}/chat/cancel`
  const body: Record<string, string> = { session_id: sessionId }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    })
    if (!response.ok) {
      logger.warn(`[ChatBI] 取消任务失败: HTTP ${response.status}`)
      return {
        task_id: null,
        session_id: sessionId,
        cancelled: false,
        message: `HTTP ${response.status}`
      }
    }
    return response.json()
  } catch (error) {
    // 取消接口失败不应阻塞前端流程，降级为仅前端中止
    logger.warn('[ChatBI] 取消任务请求异常，降级为前端中止:', error)
    return { task_id: null, session_id: sessionId, cancelled: false, message: String(error) }
  }
}

// ========== 长任务提交 API ==========

/**
 * 后端 /chat/submit 返回 422 时抛出：请求的意图不属于长任务模式，
 * 前端应降级到 /chat/stream 直接 SSE。
 */
export class IntentMismatchError extends Error {
  /** 后端检测到的非数据查询意图（如 social_share、announcement） */
  detectedIntent: string

  constructor(detectedIntent: string, detail: string) {
    super(detail)
    this.name = 'IntentMismatchError'
    this.detectedIntent = detectedIntent
  }
}

import type { TaskStreamEvent, TaskSubmitRequest, TaskSubmitResponse } from '@/types/task'
import { logger } from '@/utils/logger'

// 任务流回调接口（API 层）
export interface TaskStreamCallbacks {
  onEvent?: (event: TaskStreamEvent) => void
  onError?: (error: Event) => void
  onComplete?: () => void
}

/**
 * @deprecated Phase 39 后请使用 sendChat()，此函数将在 3 个版本后移除
 *
 * 提交异步聊天任务（长任务模式）
 * POST /api/v1/unified/chat/submit
 */
export async function submitChatTask(request: TaskSubmitRequest): Promise<TaskSubmitResponse> {
  const url = `${BASE_URL}/chat/submit`
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  }
  if (tokenHolder.hasAccessToken()) {
    headers['Authorization'] = `Bearer ${tokenHolder.getAccessToken()}`
  }
  if (request.project_id) {
    headers['Cpid'] = request.project_id
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(request)
  })

  if (!response.ok) {
    if (response.status === 422) {
      // detail 可能是结构化 JSON（新格式）或字符串（旧格式），均兼容处理
      const body = (await response.json().catch(() => ({ detail: '' }))) as {
        detail?: string | { message?: string; detected_intent?: string; redirect_endpoint?: string }
      }
      const detail = body.detail
      let detectedIntent = 'unknown'
      let detailMessage = ''
      if (typeof detail === 'object' && detail !== null) {
        // 新格式：结构化 JSON，直接读取 detected_intent 字段
        detectedIntent = detail.detected_intent ?? 'unknown'
        detailMessage = detail.message ?? JSON.stringify(detail)
      } else {
        // 旧格式：字符串，fallback 到正则解析
        detailMessage = detail || ''
        const intentMatch = detailMessage.match(/^检测到\s*(\S+)\s*意图/)
        detectedIntent = intentMatch?.[1] ?? 'unknown'
      }
      throw new IntentMismatchError(detectedIntent, detailMessage)
    }
    throw new Error(`submitChatTask 失败: HTTP ${response.status}`)
  }

  return response.json()
}

/**
 * 任务 SSE 流句柄，提供 close() 方法关闭连接。
 * 替代原生 EventSource 返回值，使调用方可通过 handle.close() 终止流。
 */
export interface TaskStreamHandle {
  close(): void
}

/**
 * 建立任务 SSE 流连接（基于统一 SSEClient，token 通过 Authorization header 传递）
 * GET /api/v1/tasks/stream/{task_id}
 * 支持 Last-Event-ID 断点续传
 */
export function attachTaskStream(
  taskId: string,
  callbacks: TaskStreamCallbacks,
  lastEventId?: string,
  projectId?: string
): TaskStreamHandle {
  // URL 仅携带 project_id / last_event_id，token 走 Authorization header
  const params = new URLSearchParams()
  if (projectId) {
    params.set('project_id', projectId)
  }
  if (lastEventId) {
    params.set('last_event_id', lastEventId)
  }
  const queryString = params.toString()
  const url = `/api/v1/tasks/stream/${taskId}${queryString ? `?${queryString}` : ''}`

  const client = new SSEClient({
    url,
    method: 'GET',
    maxRetries: 0, // 单次连接，不自动重连（上层 taskRuntime 有自己的重连策略）
    getToken: () => tokenHolder.hasAccessToken() ? tokenHolder.getAccessToken() : null,
    onMessage(event) {
      let parsed: TaskStreamEvent
      try {
        parsed = JSON.parse(event.data) as TaskStreamEvent
      } catch {
        logger.warn('[attachTaskStream] JSON 解析失败:', event.data)
        return
      }

      callbacks.onEvent?.(parsed)
      if (parsed.type === 'done' || parsed.type === 'cancelled') {
        callbacks.onComplete?.()
        client.disconnect()
      }
    },
    onError() {
      callbacks.onError?.(new Event('fetch-error'))
    },
    onClose() {
      callbacks.onComplete?.()
    },
  })

  void client.connect()

  return {
    close() {
      client.disconnect()
    },
  }
}

/**
 * 获取待确认的执行计划
 * @param sessionId 会话 ID
 */
export async function getPendingPlan(sessionId: string): Promise<{
  has_pending_plan: boolean
  plan_id: string | null
  plan_data: ExecutionPlanData | null
  created_at?: number
}> {
  const url = `${BASE_URL}/plan/pending/${sessionId}`

  const response = await fetch(url, {
    method: 'GET',
    headers: buildPlanHeaders()
  })

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  return response.json()
}

// ========== 聊天附件上传 API ==========

export interface UploadResponse {
  success: boolean
  url: string // OSS URL
  filename: string // 原始文件名
  type: string // 文件类型
  size: number // 文件大小
  oss_key: string // OSS 存储路径
}

/**
 * 上传聊天附件（图片）到 OSS
 * @param file 图片文件
 * @param userId 用户 ID（可选）
 * @param sessionId 会话 ID（可选）
 */
export async function uploadChatAttachment(
  file: File,
  userId?: string,
  sessionId?: string
): Promise<UploadResponse> {
  const url = `${BASE_URL}/upload`

  const formData = new FormData()
  formData.append('file', file)

  const uploadHeaders = buildAuthHeaders()
  if (userId) {
    uploadHeaders['X-User-Id'] = userId
  }
  if (sessionId) {
    uploadHeaders['X-Session-Id'] = sessionId
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: uploadHeaders,
    body: formData
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.detail || `上传失败: HTTP ${response.status}`)
  }

  return UploadResponseSchema.parse(await response.json())
}

// ========== 临时维护公告模板 API ==========

export interface TemplateItem {
  id: number | string
  name: string // 模板名称
  language: string // 模板语言
  title: string // 标题
  content: string // 内容
  content_type: number // 图文形式，默认为1
  images: unknown // 图片，默认为null
}

export interface TemplateListResponse {
  success: boolean
  data: TemplateItem[]
  error?: string
}

export interface TemplateSaveResponse {
  success: boolean
  data?: Record<string, unknown>
  error?: string
}

// 公告服务 API 地址
const ANNOUNCEMENT_URL = '/api/v1/announcement'

/**
 * 获取临时维护公告模板列表
 * @param mcpServiceName MCP 服务名称
 */
export async function getAnnouncementTemplates(
  mcpServiceName: string = 'maintain'
): Promise<TemplateListResponse> {
  const url = `${ANNOUNCEMENT_URL}/templates?mcp_service_name=${encodeURIComponent(mcpServiceName)}`

  const response = await fetch(url, {
    method: 'GET',
    headers: buildPlanHeaders()
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.detail || `获取模板列表失败: HTTP ${response.status}`)
  }

  return response.json()
}

/**
 * 保存临时维护公告模板
 * @param name 模板名称
 * @param title 标题
 * @param content 内容
 * @param language 模板语言，默认 zh
 * @param id 模板 ID（编辑时传入）
 * @param mcpServiceName MCP 服务名称
 */
export async function saveAnnouncementTemplate(
  name: string,
  title: string,
  content: string,
  language: string = 'zh',
  id?: number | string,
  mcpServiceName: string = 'maintain'
): Promise<TemplateSaveResponse> {
  const url = `${ANNOUNCEMENT_URL}/templates`

  const body: Record<string, unknown> = {
    name,
    language,
    title,
    content,
    content_type: 1, // 默认为1
    images: null, // 默认为null
    mcp_service_name: mcpServiceName
  }
  if (id !== undefined && id !== null) {
    body.id = id
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: buildPlanHeaders(),
    body: JSON.stringify(body)
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.detail || `保存模板失败: HTTP ${response.status}`)
  }

  return response.json()
}

/**
 * 删除临时维护公告模板
 * @param templateId 模板 ID
 * @param mcpServiceName MCP 服务名称
 */
export async function deleteAnnouncementTemplate(
  templateId: number | string,
  mcpServiceName: string = 'maintain'
): Promise<TemplateSaveResponse> {
  const url = `${ANNOUNCEMENT_URL}/templates/${templateId}?mcp_service_name=${encodeURIComponent(mcpServiceName)}`

  const response = await fetch(url, {
    method: 'DELETE',
    headers: buildPlanHeaders()
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.detail || `删除模板失败: HTTP ${response.status}`)
  }

  return response.json()
}

// ========== 执行计划步骤操作 API ==========

/** 步骤修改请求 */
export interface PlanModification {
  name?: string
  description?: string
}

/**
 * 修改执行计划步骤
 */
export async function modifyExecutionStep(
  planId: string,
  stepIndex: number,
  modifications: PlanModification,
  sessionId?: string
): Promise<PlanConfirmResponse> {
  if (!modifications.name && !modifications.description) {
    throw new Error('至少需要提供 name 或 description')
  }
  const url = `${BASE_URL}/plan/${planId}/steps/${stepIndex}`
  const response = await fetch(url, {
    method: 'PUT',
    headers: buildPlanHeaders(),
    body: JSON.stringify({ ...modifications, session_id: sessionId || '' })
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.detail || `修改步骤失败: HTTP ${response.status}`)
  }
  return response.json()
}

/**
 * 跳过执行计划步骤
 */
export async function skipExecutionStep(
  planId: string,
  stepIndex: number,
  sessionId?: string
): Promise<PlanConfirmResponse> {
  const url = `${BASE_URL}/plan/${planId}/steps/${stepIndex}/skip`
  const response = await fetch(url, {
    method: 'POST',
    headers: buildPlanHeaders(),
    body: JSON.stringify({ session_id: sessionId || '' })
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.detail || `跳过步骤失败: HTTP ${response.status}`)
  }
  return response.json()
}

/**
 * 重排执行计划步骤
 */
export async function reorderExecutionSteps(
  planId: string,
  newOrder: number[],
  sessionId?: string
): Promise<PlanConfirmResponse> {
  const url = `${BASE_URL}/plan/${planId}/reorder`
  const response = await fetch(url, {
    method: 'PUT',
    headers: buildPlanHeaders(),
    body: JSON.stringify({ new_order: newOrder, session_id: sessionId || '' })
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.detail || `重排步骤失败: HTTP ${response.status}`)
  }
  return response.json()
}

/**
 * 重新执行指定步骤
 */
export async function rerunExecutionStep(
  planId: string,
  stepIndex: number,
  sessionId?: string
): Promise<PlanConfirmResponse> {
  const url = `${BASE_URL}/plan/${planId}/steps/${stepIndex}/rerun`
  const response = await fetch(url, {
    method: 'POST',
    headers: buildPlanHeaders(),
    body: JSON.stringify({ session_id: sessionId || '' })
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.detail || `重新执行失败: HTTP ${response.status}`)
  }
  return response.json()
}

export const chatbiApi = {
  sendChatMessage,
  formatDuration,
  confirmExecutionPlan,
  cancelExecutionPlan,
  cancelChatTask,
  getPendingPlan,
  uploadChatAttachment,
  getAnnouncementTemplates,
  saveAnnouncementTemplate,
  deleteAnnouncementTemplate,
  modifyExecutionStep,
  skipExecutionStep,
  reorderExecutionSteps,
  rerunExecutionStep,
  submitChatTask,
  attachTaskStream
}
